import React, { useState, useEffect, useRef, useMemo } from "react";
import supabase from "../supabaseClient";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import Protegido from "../components/Protegido";
import { useNavigate } from "react-router-dom";
import { useNavigationState } from "../context/NavigationContext";
import useLimites from "../hooks/useLimites";
import { estaPagado } from "../utils/saldoPedido";

const money = (n) => `$${Number(n || 0).toLocaleString("es-CO")}`;

export default function Clientes() {
  const { saveModuleState, getModuleState } = useNavigationState();
  const estadoGuardado = useRef(getModuleState("/clientes")).current;

  const [clientes, setClientes] = useState([]);
  const [buscar, setBuscar] = useState(estadoGuardado?.buscar || "");
  const [form, setForm] = useState(estadoGuardado?.form || {
    nombre: "", identificacion: "", telefono: "", direccion: "", email: ""
  });
  const [editando, setEditando] = useState(estadoGuardado?.editando || null);
  const [mostrarFormulario, setMostrarFormulario] = useState(estadoGuardado?.mostrarFormulario || false);
  const [loading, setLoading] = useState(true);
  const [ordenesResumen, setOrdenesResumen] = useState([]);     // 🆕 datos para segmentación
  const [segmentoFiltro, setSegmentoFiltro] = useState("todos");
  const [fichaCliente, setFichaCliente] = useState(null);       // 🆕 cliente seleccionado para ficha 360
  const [fichaData, setFichaData] = useState(null);
  const [fichaLoading, setFichaLoading] = useState(false);
  const { puedeCrearCliente, mensajeBloqueo } = useLimites();
  const navigate = useNavigate();

  // Guardar estado
  useEffect(() => {
    saveModuleState("/clientes", { buscar, form, editando, mostrarFormulario });
  }, [buscar, JSON.stringify(form), editando, mostrarFormulario, saveModuleState]);

  useEffect(() => { cargarClientes(); }, []);

  // 🆕 Resumen de órdenes para segmentación automática
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("ordenes_pedido")
        .select("cliente_id, fecha_evento, fecha_creacion, total_neto");
      setOrdenesResumen(data || []);
    })();
  }, []);

  const cargarClientes = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("clientes")
      .select("id, codigo, nombre, identificacion, telefono, direccion, email")
      .order("nombre")
      .limit(1500);
    if (data) setClientes(data);
    setLoading(false);
  };

  const generarCodigoCliente = () => {
    const codigos = clientes
      .map(c => c.codigo).filter(Boolean)
      .filter(c => c.startsWith("C") && !isNaN(parseInt(c.slice(1))))
      .map(c => parseInt(c.slice(1)));
    const siguiente = Math.max(...codigos, 1000) + 1;
    if (siguiente > 9999) {
      Swal.fire("Límite alcanzado", "No se pueden generar más códigos de cliente", "error");
      return null;
    }
    return `C${siguiente}`;
  };

  const guardarCliente = async () => {
    if (!editando && !puedeCrearCliente()) {
      const msg = mensajeBloqueo("cliente");
      return Swal.fire(msg.titulo, msg.mensaje, msg.icono);
    }
    const { nombre, identificacion, telefono, direccion, email } = form;
    if (!nombre.trim()) return Swal.fire("Campo requerido", "El nombre del cliente es obligatorio.", "warning");

    const clienteCompleto = {
      nombre: nombre.trim(),
      identificacion: identificacion?.trim() || null,
      telefono: telefono?.trim() || null,
      direccion: direccion?.trim() || null,
      email: email?.trim() || null
    };

    if (!editando && clienteCompleto.identificacion) {
      const { data: existentes } = await supabase
        .from("clientes").select("id").eq("identificacion", clienteCompleto.identificacion);
      if (existentes && existentes.length > 0) {
        return Swal.fire("Ya existe", "Ya hay un cliente con esa identificación.", "warning");
      }
    }

    if (editando) {
      const { error } = await supabase.from("clientes").update(clienteCompleto).eq("id", editando);
      if (!error) {
        Swal.fire({ icon: "success", title: "Actualizado", timer: 1500, showConfirmButton: false });
        cerrarFormulario();
        cargarClientes();
      } else Swal.fire("Error", "No se pudo actualizar el cliente.", "error");
    } else {
      const nuevoCodigo = generarCodigoCliente();
      if (!nuevoCodigo) return;
      const { error } = await supabase.from("clientes").insert([{ codigo: nuevoCodigo, ...clienteCompleto }]);
      if (!error) {
        Swal.fire({ icon: "success", title: "Cliente guardado", timer: 1500, showConfirmButton: false });
        cerrarFormulario();
        cargarClientes();
      } else Swal.fire("Error", "No se pudo guardar el cliente.", "error");
    }
  };

  const editarCliente = (cliente) => {
    setEditando(cliente.id);
    setForm({
      nombre: cliente.nombre || "",
      identificacion: cliente.identificacion || "",
      telefono: cliente.telefono || "",
      direccion: cliente.direccion || "",
      email: cliente.email || ""
    });
    setMostrarFormulario(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cerrarFormulario = () => {
    setEditando(null);
    setForm({ nombre: "", identificacion: "", telefono: "", direccion: "", email: "" });
    setMostrarFormulario(false);
  };

  const eliminarCliente = async (id) => {
    const { isConfirmed } = await Swal.fire({
      title: "¿Eliminar este cliente?",
      text: "Esta acción no se puede deshacer",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      confirmButtonColor: "#ef4444",
      cancelButtonText: "Cancelar",
    });
    if (!isConfirmed) return;
    const { error } = await supabase.from("clientes").delete().eq("id", id);
    if (!error) {
      Swal.fire({ icon: "success", title: "Eliminado", timer: 1500, showConfirmButton: false });
      cargarClientes();
    }
  };

  const borrarTodo = async () => {
    if (clientes.length === 0) return Swal.fire("Sin datos", "No hay clientes para eliminar.", "info");
    const { isConfirmed } = await Swal.fire({
      title: "⚠️ ¿Eliminar TODOS los clientes?",
      html: `<p>Esta acción borrará <strong>${clientes.length} cliente(s)</strong> y no se puede deshacer.</p>
             <p style="color:#ef4444;font-weight:600;margin-top:8px;">Los documentos asociados NO se eliminarán.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar todo",
      confirmButtonColor: "#ef4444",
      cancelButtonText: "Cancelar",
    });
    if (!isConfirmed) return;
    const { error } = await supabase.from("clientes").delete().neq("id", "00000000-0000-0000-0000-000000000000");
    if (!error) {
      Swal.fire({ icon: "success", title: "Todos los clientes eliminados", timer: 2000, showConfirmButton: false });
      cargarClientes();
    } else Swal.fire("Error", "No se pudieron eliminar los clientes. Puede haber documentos asociados.", "error");
  };

  /* ─── Exportar Excel (.xlsx) ─── */
  const exportarExcel = () => {
    if (clientes.length === 0) return Swal.fire("Sin datos", "No hay clientes para exportar.", "info");

    const datosLimpios = clientes.map((c, i) => ({
      "#": i + 1,
      "Código": c.codigo || "",
      "Nombre": c.nombre || "",
      "Identificación": c.identificacion || "",
      "Teléfono": c.telefono || "",
      "Dirección": c.direccion || "",
      "Email": c.email || ""
    }));

    const ws = XLSX.utils.json_to_sheet(datosLimpios);
    // Ancho de columnas
    ws["!cols"] = [
      { wch: 5 }, { wch: 10 }, { wch: 30 }, { wch: 18 },
      { wch: 15 }, { wch: 35 }, { wch: 28 }
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Clientes");
    const fecha = new Date().toLocaleDateString("es-CO").replaceAll("/", "-");
    XLSX.writeFile(wb, `clientes_${fecha}.xlsx`);
    Swal.fire({ icon: "success", title: "Excel descargado", timer: 1500, showConfirmButton: false });
  };

  /* ─── Importar desde Excel ─── */
  const importarDesdeExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    event.target.value = "";

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

      // Mapeo flexible de columnas (acepta variantes)
      const mapCol = (row, opciones) => {
        for (const key of opciones) {
          const val = row[key] || row[key.toLowerCase()] || row[key.toUpperCase()];
          if (val !== undefined && val !== "") return String(val).trim();
        }
        return "";
      };

      const clientesValidos = json
        .map((row) => ({
          nombre: mapCol(row, ["nombre", "Nombre", "NOMBRE", "name", "Name"]),
          identificacion: mapCol(row, ["identificacion", "Identificación", "Identificacion", "IDENTIFICACION", "cedula", "Cedula", "CC", "NIT", "nit"]),
          telefono: mapCol(row, ["telefono", "Teléfono", "Telefono", "TELEFONO", "cel", "celular", "Celular"]),
          direccion: mapCol(row, ["direccion", "Dirección", "Direccion", "DIRECCION", "address"]),
          email: mapCol(row, ["email", "Email", "EMAIL", "correo", "Correo", "CORREO"])
        }))
        .filter((c) => c.nombre);

      if (clientesValidos.length === 0) {
        return Swal.fire("Sin datos válidos", "No se encontraron clientes con nombre. Revisa que la columna 'nombre' exista.", "error");
      }

      // Generar códigos automáticos
      let maxCodigo = Math.max(
        ...clientes.map(c => c.codigo).filter(Boolean)
          .filter(c => c.startsWith("C") && !isNaN(parseInt(c.slice(1))))
          .map(c => parseInt(c.slice(1))),
        1000
      );

      const clientesConCodigo = clientesValidos.map((c) => {
        maxCodigo++;
        return {
          codigo: `C${maxCodigo}`,
          nombre: c.nombre,
          identificacion: c.identificacion || null,
          telefono: c.telefono || null,
          direccion: c.direccion || null,
          email: c.email || null,
        };
      });

      // Insertar uno por uno para saltar duplicados sin detener toda la importación
      let importados = 0;
      let errores = 0;
      for (const cliente of clientesConCodigo) {
        const { error } = await supabase.from("clientes").insert([cliente]);
        if (error) {
          console.warn(`⚠️ No se importó "${cliente.nombre}":`, error.message);
          errores++;
        } else {
          importados++;
        }
      }

      if (importados === 0) {
        return Swal.fire("Error", "No se pudo importar ningún cliente. Revisa que no haya emails o identificaciones duplicadas.", "error");
      }

      const msgExtra = errores > 0 ? `\n⚠️ ${errores} cliente(s) no se importaron por datos duplicados.` : "";
      Swal.fire("Importación exitosa", `${importados} cliente(s) importados correctamente.${msgExtra}`, importados > 0 && errores > 0 ? "warning" : "success");
      cargarClientes();
    } catch (err) {
      console.error(err);
      Swal.fire("Error al leer archivo", "Verifica que sea un archivo Excel (.xlsx) válido.", "error");
    }
  };

  /* ─── Guía de importación ─── */
  const mostrarGuia = () => {
    Swal.fire({
      title: "📥 Guía de importación desde Excel",
      html: `
        <div style="text-align:left;font-size:13px;line-height:1.7;">
          <p>Tu archivo Excel debe tener estas columnas en la <strong>primera fila</strong>:</p>
          <table style="width:100%;border-collapse:collapse;margin:10px 0;font-size:12px;">
            <tr style="background:#f0f9ff;">
              <th style="border:1px solid #e5e7eb;padding:6px;text-align:left;">Columna</th>
              <th style="border:1px solid #e5e7eb;padding:6px;text-align:left;">¿Obligatoria?</th>
              <th style="border:1px solid #e5e7eb;padding:6px;text-align:left;">Ejemplo</th>
            </tr>
            <tr>
              <td style="border:1px solid #e5e7eb;padding:6px;font-weight:600;">nombre</td>
              <td style="border:1px solid #e5e7eb;padding:6px;color:#ef4444;">✅ Sí</td>
              <td style="border:1px solid #e5e7eb;padding:6px;">María López</td>
            </tr>
            <tr>
              <td style="border:1px solid #e5e7eb;padding:6px;">identificacion</td>
              <td style="border:1px solid #e5e7eb;padding:6px;color:#9ca3af;">Opcional</td>
              <td style="border:1px solid #e5e7eb;padding:6px;">1234567890</td>
            </tr>
            <tr>
              <td style="border:1px solid #e5e7eb;padding:6px;">telefono</td>
              <td style="border:1px solid #e5e7eb;padding:6px;color:#9ca3af;">Opcional</td>
              <td style="border:1px solid #e5e7eb;padding:6px;">3001234567</td>
            </tr>
            <tr>
              <td style="border:1px solid #e5e7eb;padding:6px;">direccion</td>
              <td style="border:1px solid #e5e7eb;padding:6px;color:#9ca3af;">Opcional</td>
              <td style="border:1px solid #e5e7eb;padding:6px;">Calle 10 #5-20</td>
            </tr>
            <tr>
              <td style="border:1px solid #e5e7eb;padding:6px;">email</td>
              <td style="border:1px solid #e5e7eb;padding:6px;color:#9ca3af;">Opcional</td>
              <td style="border:1px solid #e5e7eb;padding:6px;">maria@email.com</td>
            </tr>
          </table>
          <p style="margin-top:10px;">💡 <strong>Tips:</strong></p>
          <ul style="margin:4px 0 0 16px;padding:0;">
            <li>El sistema acepta variantes como "Nombre", "NOMBRE", "Teléfono", "celular", etc.</li>
            <li>Las filas sin nombre serán ignoradas.</li>
            <li>Se asigna código automático a cada cliente.</li>
            <li>Formatos aceptados: <strong>.xlsx</strong> y <strong>.xls</strong></li>
          </ul>
        </div>
      `,
      width: 560,
      confirmButtonText: "Entendido",
      confirmButtonColor: "#0077B6"
    });
  };

  // 🆕 ─── Ficha 360° del cliente ───
  const abrirFicha = async (cliente) => {
    setFichaCliente(cliente);
    setFichaLoading(true);
    setFichaData(null);
    try {
      const [{ data: ords }, { data: cots }, { data: cit }] = await Promise.all([
        supabase.from("ordenes_pedido").select("id, numero, fecha_creacion, fecha_evento, total, total_neto, productos, estado, abonos, abonos_recepcion, tipo_evento, lugar_evento, cerrada").eq("cliente_id", cliente.id).order("fecha_evento", { ascending: false }),
        supabase.from("cotizaciones").select("id, numero, fecha, fecha_evento, total_neto, estado, tipo_evento").eq("cliente_id", cliente.id).order("fecha_evento", { ascending: false }),
        supabase.from("citas").select("id, fecha, hora, estado, motivo").eq("cliente_id", cliente.id).order("fecha", { ascending: false }),
      ]);
      const ordenes = ords || [];
      const cotizaciones = cots || [];
      const citas = cit || [];

      // Métricas
      const totalRecaudo = ordenes.reduce((a, o) => a + Number(o.total_neto || 0), 0);
      const totalOrdenes = ordenes.length;
      const ticketPromedio = totalOrdenes > 0 ? Math.round(totalRecaudo / totalOrdenes) : 0;
      const fechasOrd = ordenes.map(o => new Date(o.fecha_evento || o.fecha_creacion).getTime()).filter(f => f > 0);
      const primeraOrden = fechasOrd.length > 0 ? new Date(Math.min(...fechasOrd)) : null;
      const ultimaOrden = fechasOrd.length > 0 ? new Date(Math.max(...fechasOrd)) : null;
      const diasDesdeUltima = ultimaOrden ? Math.round((Date.now() - ultimaOrden.getTime()) / 86400000) : null;
      // Se calcula, no se lee el campo "estado": ese campo solo se actualiza
      // cuando alguien guarda el documento, así que un pedido cobrado en
      // Recepción se quedaba marcado como pendiente para siempre.
      const pctPagado = totalOrdenes > 0 ? Math.round((ordenes.filter(o => estaPagado(o)).length / totalOrdenes) * 100) : 0;
      const totalDocs = totalOrdenes + cotizaciones.length;
      const conversionRate = totalDocs > 0 ? Math.round((totalOrdenes / totalDocs) * 100) : 0;

      // Productos favoritos
      const prodMap = {};
      ordenes.forEach(o => {
        (o.productos || []).forEach(p => {
          const nombre = p.descripcion || p.nombre || "Sin nombre";
          const key = nombre.toLowerCase();
          if (!prodMap[key]) prodMap[key] = { nombre, cantidad: 0, veces: 0 };
          prodMap[key].cantidad += Number(p.cantidad || 0);
          prodMap[key].veces++;
        });
      });
      const productosFavoritos = Object.values(prodMap).sort((a, b) => b.veces - a.veces).slice(0, 5);

      // Tipos de evento
      const tipoMap = {};
      ordenes.forEach(o => {
        const t = (o.tipo_evento || "").trim();
        if (t) tipoMap[t] = (tipoMap[t] || 0) + 1;
      });
      const tiposEvento = Object.entries(tipoMap).sort((a, b) => b[1] - a[1]).map(([tipo, count]) => ({ tipo, count }));

      setFichaData({
        ordenes, cotizaciones, citas,
        totalRecaudo, totalOrdenes, ticketPromedio,
        primeraOrden, ultimaOrden, diasDesdeUltima,
        pctPagado, conversionRate,
        productosFavoritos, tiposEvento,
      });
    } catch (err) { console.error("Error cargando ficha:", err); }
    setFichaLoading(false);
  };

  // 🆕 ─── Segmentación automática RFM ───
  const SEGMENTOS_DEF = [
    { id: "vip", label: "VIP", color: "#F59E0B", emoji: "⭐", tip: "4+ pedidos, último hace menos de 3 meses" },
    { id: "frecuente", label: "Frecuente", color: "#3B82F6", emoji: "🔄", tip: "3+ pedidos, último hace menos de 6 meses" },
    { id: "nuevo", label: "Nuevo", color: "#10B981", emoji: "🆕", tip: "1 pedido reciente (menos de 3 meses)" },
    { id: "en_riesgo", label: "Riesgo", color: "#EF4444", emoji: "⚠️", tip: "2+ pedidos pero lleva 3–9 meses sin volver" },
    { id: "dormido", label: "Dormido", color: "#6B7280", emoji: "😴", tip: "Último pedido hace más de 9 meses" },
    { id: "ocasional", label: "Ocasional", color: "#8B5CF6", emoji: "💜", tip: "1–2 pedidos, ni nuevo ni dormido" },
    { id: "sin_pedidos", label: "Sin pedidos", color: "#D1D5DB", emoji: "⬜", tip: "Registrado en el sistema pero aún sin pedidos confirmados" },
  ];

  const segmentos = useMemo(() => {
    const hoy = Date.now();
    const porCliente = {};
    (ordenesResumen || []).forEach((o) => {
      if (!o.cliente_id) return;
      if (!porCliente[o.cliente_id]) porCliente[o.cliente_id] = [];
      porCliente[o.cliente_id].push(o);
    });

    const clasificar = (ords) => {
      if (!ords || ords.length === 0) return "sin_pedidos";
      const n = ords.length;
      const fechas = ords.map((o) => new Date(o.fecha_evento || o.fecha_creacion).getTime()).filter((f) => f > 0);
      const ultima = fechas.length > 0 ? Math.max(...fechas) : 0;
      const dias = ultima > 0 ? Math.round((hoy - ultima) / 86400000) : 999;
      if (n >= 4 && dias < 90) return "vip";
      if (n >= 3 && dias < 180) return "frecuente";
      if (n === 1 && dias < 90) return "nuevo";
      if (n >= 2 && dias >= 90 && dias < 270) return "en_riesgo";
      if (dias >= 270) return "dormido";
      return "ocasional";
    };

    const mapa = {};
    const conteo = { todos: 0 };
    SEGMENTOS_DEF.forEach((s) => (conteo[s.id] = 0));
    (clientes || []).forEach((c) => {
      const seg = clasificar(porCliente[c.id]);
      mapa[c.id] = seg;
      conteo[seg] = (conteo[seg] || 0) + 1;
      conteo.todos++;
    });

    return { mapa, conteo };
  }, [clientes, ordenesResumen]);

  const getSegDef = (id) => SEGMENTOS_DEF.find((s) => s.id === id) || SEGMENTOS_DEF[6];

  /* ─── Filtrar ─── */
  const filtrados = clientes.filter((c) => {
    const matchBuscar = !buscar.trim() ? true :
      [c.codigo, c.nombre, c.identificacion, c.telefono, c.direccion, c.email]
        .some((campo) => campo?.toLowerCase().includes(buscar.toLowerCase()));
    const matchSegmento = segmentoFiltro === "todos" || segmentos.mapa[c.id] === segmentoFiltro;
    return matchBuscar && matchSegmento;
  });

  /* ═══ RENDER ═══ */
  return (
    <Protegido>
      <div className="sw-pagina">
        <div className="sw-pagina-contenido" style={{ maxWidth: 900 }}>

          {/* ═══ HEADER ═══ */}
          <div className="sw-header">
            <h1 className="sw-header-titulo">👥 Gestión de Clientes</h1>
          </div>

          {/* ═══ KPI CARDS ═══ */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: 12,
            marginBottom: 20
          }}>
            <div className="sw-card" style={{ padding: "16px 14px", textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "var(--sw-texto-terciario)", fontWeight: 500 }}>Total clientes</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: "var(--sw-azul)", marginTop: 4 }}>{clientes.length}</div>
            </div>
            <div className="sw-card" style={{ padding: "16px 14px", textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "var(--sw-texto-terciario)", fontWeight: 500 }}>Con email</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: "var(--sw-verde)", marginTop: 4 }}>
                {clientes.filter(c => c.email).length}
              </div>
            </div>
            <div className="sw-card" style={{ padding: "16px 14px", textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "var(--sw-texto-terciario)", fontWeight: 500 }}>Con identificación</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: "var(--sw-morado)", marginTop: 4 }}>
                {clientes.filter(c => c.identificacion).length}
              </div>
            </div>
          </div>

          {/* ═══ SEGMENTACIÓN ═══ */}
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 14, alignItems: "center" }}>
            <button
              onClick={() => setSegmentoFiltro("todos")}
              title="Mostrar todos los clientes"
              style={{
                padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                border: segmentoFiltro === "todos" ? "2px solid #0077B6" : "1px solid var(--sw-borde)",
                background: segmentoFiltro === "todos" ? "#E0F2FE" : "white",
                color: segmentoFiltro === "todos" ? "#0077B6" : "var(--sw-texto-secundario)",
                cursor: "pointer",
              }}
            >
              Todos ({segmentos.conteo.todos || 0})
            </button>
            {SEGMENTOS_DEF.map((s) => {
              const n = segmentos.conteo[s.id] || 0;
              if (n === 0) return null;
              const activo = segmentoFiltro === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setSegmentoFiltro(activo ? "todos" : s.id)}
                  title={s.tip}
                  style={{
                    padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                    border: activo ? `2px solid ${s.color}` : "1px solid var(--sw-borde)",
                    background: activo ? `${s.color}18` : "white",
                    color: activo ? s.color : "var(--sw-texto-secundario)",
                    cursor: "pointer", transition: "all 0.15s",
                  }}
                >
                  {s.emoji} {s.label} ({n})
                </button>
              );
            })}
          </div>

          {/* ═══ ACCIONES ═══ */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
            <button className="sw-btn sw-btn-primario" onClick={() => { cerrarFormulario(); setMostrarFormulario(true); }}>
              ＋ Nuevo cliente
            </button>
            <button className="sw-btn sw-btn-secundario" onClick={exportarExcel}>
              📊 Exportar Excel
            </button>
            <button className="sw-btn sw-btn-secundario" onClick={() => document.getElementById("archivoExcelClientes").click()}>
              📥 Importar Excel
            </button>
            <button className="sw-btn sw-btn-secundario" onClick={mostrarGuia}>
              ❓ Guía importación
            </button>
            <button className="sw-btn" style={{ background: "#fef2f2", color: "#ef4444", border: "1px solid #fecaca" }} onClick={borrarTodo}>
              🗑️ Eliminar todo
            </button>
          </div>

          <input type="file" accept=".xlsx,.xls" onChange={importarDesdeExcel} id="archivoExcelClientes" style={{ display: "none" }} />

          {/* ═══ FORMULARIO ═══ */}
          {mostrarFormulario && (
            <div className="sw-card" style={{ marginBottom: 16 }}>
              <div className="sw-card-header sw-card-header-cyan">
                <h3 className="sw-card-titulo" style={{ color: "white", margin: 0 }}>
                  {editando ? "✏️ Editar Cliente" : "➕ Nuevo Cliente"}
                </h3>
              </div>
              <div className="sw-card-body">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "var(--sw-texto-secundario)", marginBottom: 4, display: "block" }}>Nombre *</label>
                    <input type="text" placeholder="Nombre completo" value={form.nombre}
                      onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                      style={{ width: "100%", padding: "10px 12px", border: "1px solid var(--sw-borde)", borderRadius: 8, fontSize: 14, boxSizing: "border-box" }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "var(--sw-texto-secundario)", marginBottom: 4, display: "block" }}>Identificación</label>
                    <input type="text" placeholder="Documento" value={form.identificacion}
                      onChange={(e) => setForm({ ...form, identificacion: e.target.value })}
                      style={{ width: "100%", padding: "10px 12px", border: "1px solid var(--sw-borde)", borderRadius: 8, fontSize: 14, boxSizing: "border-box" }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "var(--sw-texto-secundario)", marginBottom: 4, display: "block" }}>Teléfono</label>
                    <input type="text" placeholder="300 123 4567" value={form.telefono}
                      onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                      style={{ width: "100%", padding: "10px 12px", border: "1px solid var(--sw-borde)", borderRadius: 8, fontSize: 14, boxSizing: "border-box" }}
                    />
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "var(--sw-texto-secundario)", marginBottom: 4, display: "block" }}>Dirección</label>
                    <input type="text" placeholder="Dirección del cliente" value={form.direccion}
                      onChange={(e) => setForm({ ...form, direccion: e.target.value })}
                      style={{ width: "100%", padding: "10px 12px", border: "1px solid var(--sw-borde)", borderRadius: 8, fontSize: 14, boxSizing: "border-box" }}
                    />
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "var(--sw-texto-secundario)", marginBottom: 4, display: "block" }}>Email</label>
                    <input type="email" placeholder="correo@ejemplo.com" value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      style={{ width: "100%", padding: "10px 12px", border: "1px solid var(--sw-borde)", borderRadius: 8, fontSize: 14, boxSizing: "border-box" }}
                    />
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                  <button className="sw-btn sw-btn-primario" style={{ flex: 1 }} onClick={guardarCliente}>
                    {editando ? "💾 Actualizar" : "💾 Guardar"}
                  </button>
                  <button className="sw-btn sw-btn-secundario" style={{ flex: 1 }} onClick={cerrarFormulario}>
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ═══ BÚSQUEDA ═══ */}
          <div className="sw-card" style={{ marginBottom: 16 }}>
            <div className="sw-card-body" style={{ padding: "12px 16px" }}>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 16, color: "var(--sw-texto-terciario)" }}>🔍</span>
                <input
                  type="text"
                  placeholder="Buscar por nombre, código, teléfono, dirección, email..."
                  value={buscar}
                  onChange={(e) => setBuscar(e.target.value)}
                  style={{
                    width: "100%", padding: "10px 12px 10px 38px",
                    border: "1px solid var(--sw-borde)", borderRadius: 8,
                    fontSize: 14, boxSizing: "border-box",
                    background: "var(--sw-fondo)"
                  }}
                />
              </div>
              {buscar && (
                <div style={{ fontSize: 12, color: "var(--sw-texto-terciario)", marginTop: 6 }}>
                  {filtrados.length} resultado{filtrados.length !== 1 ? "s" : ""} de {clientes.length} clientes
                </div>
              )}
            </div>
          </div>

          {/* ═══ LISTADO ═══ */}
          <div className="sw-card">
            <div className="sw-card-header">
              <h3 className="sw-card-titulo">
                📋 {buscar ? "Resultados" : "Todos los clientes"} ({filtrados.length})
              </h3>
            </div>
            <div className="sw-card-body" style={{ padding: 0 }}>
              {loading ? (
                <div style={{ padding: 40, textAlign: "center", color: "var(--sw-texto-terciario)" }}>
                  Cargando clientes...
                </div>
              ) : filtrados.length === 0 ? (
                <div style={{ padding: 40, textAlign: "center" }}>
                  <div style={{ fontSize: 40, marginBottom: 8 }}>👥</div>
                  <div style={{ fontSize: 14, color: "var(--sw-texto-terciario)" }}>
                    {buscar ? `No se encontraron resultados para "${buscar}"` : "No hay clientes registrados"}
                  </div>
                  {!buscar && (
                    <p style={{ fontSize: 12, color: "var(--sw-texto-terciario)", marginTop: 6 }}>
                      Agrega tu primer cliente o importa desde Excel
                    </p>
                  )}
                </div>
              ) : (
                filtrados.map((c) => (
                  <div key={c.id} style={{
                    padding: "14px 16px",
                    borderBottom: "1px solid #f3f4f6",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 10,
                    transition: "background 0.15s",
                  }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#f9fafb"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                  >
                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span
                          style={{ fontWeight: 600, fontSize: 14, color: "var(--sw-azul)", cursor: "pointer" }}
                          onClick={() => abrirFicha(c)}
                          title="Ver ficha completa del cliente"
                        >
                          {c.nombre}
                        </span>
                        {c.codigo && (
                          <span style={{
                            fontSize: 11, padding: "2px 8px", borderRadius: 20,
                            background: "var(--sw-cyan-muy-claro)", color: "var(--sw-azul)",
                            fontWeight: 500
                          }}>
                            {c.codigo}
                          </span>
                        )}
                        {segmentos.mapa[c.id] && segmentos.mapa[c.id] !== "sin_pedidos" && (() => {
                          const s = getSegDef(segmentos.mapa[c.id]);
                          return (
                            <span style={{
                              fontSize: 10, padding: "2px 8px", borderRadius: 20,
                              background: `${s.color}15`, color: s.color,
                              fontWeight: 600, letterSpacing: 0.3,
                            }}>
                              {s.emoji} {s.label}
                            </span>
                          );
                        })()}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--sw-texto-terciario)", marginTop: 3, display: "flex", flexWrap: "wrap", gap: "4px 12px" }}>
                        {c.identificacion && <span>🆔 {c.identificacion}</span>}
                        {c.telefono && <span>📞 {c.telefono}</span>}
                        {c.email && <span>📧 {c.email}</span>}
                        {c.direccion && <span>📍 {c.direccion}</span>}
                      </div>
                    </div>

                    {/* Acciones */}
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <button
                        className="sw-btn-icono"
                        title="Ver pedidos y cotizaciones de este cliente"
                        onClick={() => {
                          saveModuleState("/buscar-documento", {
                            clienteSeleccionado: { id: c.id, nombre: c.nombre },
                            clienteBusqueda: c.nombre,
                            mostrarResultados: true,
                          });
                          navigate("/buscar-documento");
                        }}
                      >
                        🔍
                      </button>
                      <button className="sw-btn-icono" onClick={() => editarCliente(c)} title="Editar">
                        ✏️
                      </button>
                      <button className="sw-btn-icono" onClick={() => eliminarCliente(c.id)} title="Eliminar"
                        style={{ color: "#ef4444" }}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

        </div>
      </div>

      {/* ═══ MODAL FICHA 360° ═══ */}
      {fichaCliente && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
          display: "flex", justifyContent: "center", alignItems: "flex-start",
          zIndex: 9999, paddingTop: 30, overflowY: "auto",
        }} onClick={() => { setFichaCliente(null); setFichaData(null); }}>
          <div style={{
            background: "white", borderRadius: 16, width: "95%", maxWidth: 720,
            maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          }} onClick={(e) => e.stopPropagation()}>

            {/* Header */}
            <div style={{
              padding: "20px 24px 16px", borderBottom: "1px solid #f3f4f6",
              display: "flex", justifyContent: "space-between", alignItems: "flex-start",
            }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <h2 style={{ margin: 0, fontSize: 20, color: "var(--sw-texto)" }}>{fichaCliente.nombre}</h2>
                  {fichaCliente.codigo && (
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: "var(--sw-cyan-muy-claro)", color: "var(--sw-azul)", fontWeight: 500 }}>
                      {fichaCliente.codigo}
                    </span>
                  )}
                  {segmentos.mapa[fichaCliente.id] && segmentos.mapa[fichaCliente.id] !== "sin_pedidos" && (() => {
                    const s = getSegDef(segmentos.mapa[fichaCliente.id]);
                    return <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: `${s.color}15`, color: s.color, fontWeight: 600 }}>{s.emoji} {s.label}</span>;
                  })()}
                </div>
                <div style={{ fontSize: 13, color: "var(--sw-texto-terciario)", marginTop: 6, display: "flex", gap: 14, flexWrap: "wrap" }}>
                  {fichaCliente.telefono && <span>📞 {fichaCliente.telefono}</span>}
                  {fichaCliente.email && <span>📧 {fichaCliente.email}</span>}
                  {fichaCliente.direccion && <span>📍 {fichaCliente.direccion}</span>}
                  {fichaCliente.identificacion && <span>🆔 {fichaCliente.identificacion}</span>}
                </div>
              </div>
              <button onClick={() => { setFichaCliente(null); setFichaData(null); }} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#9ca3af", padding: 4 }}>✕</button>
            </div>

            {/* Contenido */}
            <div style={{ padding: "16px 24px 24px" }}>
              {fichaLoading ? (
                <div style={{ textAlign: "center", padding: 40, color: "var(--sw-texto-terciario)" }}>Cargando información...</div>
              ) : fichaData ? (
                <>
                  {/* KPIs */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10, marginBottom: 20 }}>
                    {[
                      { label: "Recaudo total", valor: money(fichaData.totalRecaudo), color: "#10b981" },
                      { label: "Pedidos", valor: fichaData.totalOrdenes, color: "#0077B6" },
                      { label: "Ticket promedio", valor: money(fichaData.ticketPromedio), color: "#8b5cf6" },
                      { label: "Días sin pedir", valor: fichaData.diasDesdeUltima !== null ? fichaData.diasDesdeUltima : "—", color: fichaData.diasDesdeUltima > 180 ? "#ef4444" : "#0077B6" },
                      { label: "Conversión", valor: `${fichaData.conversionRate}%`, color: "#f59e0b" },
                      { label: "Pagado a tiempo", valor: `${fichaData.pctPagado}%`, color: fichaData.pctPagado >= 70 ? "#10b981" : "#ef4444" },
                    ].map((k, i) => (
                      <div key={i} style={{ background: "#f9fafb", borderRadius: 10, padding: "12px 14px", textAlign: "center", borderLeft: `3px solid ${k.color}` }}>
                        <div style={{ fontSize: 11, color: "var(--sw-texto-terciario)", fontWeight: 500 }}>{k.label}</div>
                        <div style={{ fontSize: 22, fontWeight: 700, color: k.color, marginTop: 2 }}>{k.valor}</div>
                      </div>
                    ))}
                  </div>

                  {/* Dos columnas: Productos favoritos + Tipos de evento */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                    {/* Productos favoritos */}
                    <div>
                      <h4 style={{ fontSize: 13, fontWeight: 600, color: "var(--sw-texto-secundario)", marginBottom: 8 }}>📦 Productos favoritos</h4>
                      {fichaData.productosFavoritos.length === 0 ? (
                        <div style={{ fontSize: 13, color: "var(--sw-texto-terciario)" }}>Sin datos</div>
                      ) : fichaData.productosFavoritos.map((p, i) => (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid #f3f4f6", fontSize: 13 }}>
                          <span style={{ color: "var(--sw-texto)" }}>{p.nombre}</span>
                          <span style={{ color: "var(--sw-texto-terciario)", fontWeight: 600 }}>{p.veces}x ({p.cantidad} uds)</span>
                        </div>
                      ))}
                    </div>
                    {/* Tipos de evento */}
                    <div>
                      <h4 style={{ fontSize: 13, fontWeight: 600, color: "var(--sw-texto-secundario)", marginBottom: 8 }}>🎉 Tipos de evento</h4>
                      {fichaData.tiposEvento.length === 0 ? (
                        <div style={{ fontSize: 13, color: "var(--sw-texto-terciario)" }}>Sin datos</div>
                      ) : fichaData.tiposEvento.map((t, i) => (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid #f3f4f6", fontSize: 13 }}>
                          <span>{t.tipo}</span>
                          <span style={{ fontWeight: 600, color: "var(--sw-azul)" }}>{t.count} pedidos</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Últimos pedidos */}
                  <h4 style={{ fontSize: 13, fontWeight: 600, color: "var(--sw-texto-secundario)", marginBottom: 8 }}>📋 Últimos pedidos</h4>
                  {fichaData.ordenes.length === 0 ? (
                    <div style={{ fontSize: 13, color: "var(--sw-texto-terciario)", marginBottom: 16 }}>Sin pedidos registrados</div>
                  ) : (
                    <div style={{ marginBottom: 16 }}>
                      {fichaData.ordenes.slice(0, 6).map((o, i) => (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: "1px solid #f3f4f6", fontSize: 13 }}>
                          <div>
                            <span style={{ fontWeight: 600, color: "var(--sw-azul)" }}>{o.numero || "—"}</span>
                            <span style={{ marginLeft: 8, color: "var(--sw-texto-terciario)" }}>
                              {o.fecha_evento ? new Date(o.fecha_evento).toLocaleDateString("es-CO") : "—"}
                            </span>
                            {o.tipo_evento && <span style={{ marginLeft: 8, fontSize: 11, color: "#8b5cf6" }}>({o.tipo_evento})</span>}
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{ fontWeight: 600, color: estaPagado(o) ? "var(--sw-verde)" : "var(--sw-texto)" }}>{money(o.total_neto)}</span>
                            <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 10, background: estaPagado(o) ? "#dcfce7" : "#fef9c3", color: estaPagado(o) ? "#16a34a" : "#ca8a04", fontWeight: 600 }}>
                              {estaPagado(o) ? "pagado" : "pendiente"}
                            </span>
                          </div>
                        </div>
                      ))}
                      {fichaData.ordenes.length > 6 && (
                        <div style={{ fontSize: 12, color: "var(--sw-texto-terciario)", marginTop: 6, textAlign: "center" }}>
                          y {fichaData.ordenes.length - 6} pedidos más...
                        </div>
                      )}
                    </div>
                  )}

                  {/* Acciones rápidas */}
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", paddingTop: 10, borderTop: "1px solid #f3f4f6" }}>
                    {fichaCliente.telefono && (
                      <a href={`https://wa.me/57${fichaCliente.telefono.replace(/\D/g, "")}`} target="_blank" rel="noreferrer"
                        style={{ padding: "8px 14px", borderRadius: 8, background: "#25D366", color: "white", fontSize: 13, fontWeight: 600, textDecoration: "none" }}>
                        💬 WhatsApp
                      </a>
                    )}
                    {fichaCliente.telefono && (
                      <a href={`tel:${fichaCliente.telefono}`}
                        style={{ padding: "8px 14px", borderRadius: 8, background: "#0077B6", color: "white", fontSize: 13, fontWeight: 600, textDecoration: "none" }}>
                        📞 Llamar
                      </a>
                    )}
                    <button onClick={() => {
                      saveModuleState("/buscar-documento", { clienteSeleccionado: { id: fichaCliente.id, nombre: fichaCliente.nombre }, clienteBusqueda: fichaCliente.nombre, mostrarResultados: true });
                      navigate("/buscar-documento");
                    }} style={{ padding: "8px 14px", borderRadius: 8, background: "var(--sw-cyan-muy-claro)", color: "var(--sw-azul)", fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer" }}>
                      🔍 Ver documentos
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

    </Protegido>
  );
}